import {
  deriveKey as deriveKeyArgon2,
  generateSalt,
  DEFAULT_KDF_PARAMS,
  serializeKdfParams,
} from "./argon2";
import { arrayBufferToBase64, base64ToArrayBuffer } from "../utils/encoding";

/**
 * PBKDF2 KDF params (used by MCP server)
 */
interface Pbkdf2Params {
  type: "PBKDF2";
  iterations: number;
  hash: string;
  hashLength: number;
}

/**
 * Derive key using PBKDF2 (for MCP-created shares)
 */
async function deriveKeyPbkdf2(
  passcode: string,
  salt: Uint8Array,
  iterations: number = 100000,
): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const passcodeKey = await crypto.subtle.importKey(
    "raw",
    encoder.encode(passcode),
    "PBKDF2",
    false,
    ["deriveBits"],
  );

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      salt: salt as BufferSource,
      iterations,
      hash: "SHA-256",
    },
    passcodeKey,
    256,
  );

  return new Uint8Array(derivedBits);
}

/**
 * Detect KDF type and derive key accordingly
 */
async function deriveKey(
  passcode: string,
  salt: Uint8Array,
  params: Record<string, unknown>,
): Promise<Uint8Array> {
  // Check if this is a PBKDF2 share (created by MCP server)
  if (params.type === "PBKDF2") {
    const pbkdf2Params = params as unknown as Pbkdf2Params;
    return deriveKeyPbkdf2(passcode, salt, pbkdf2Params.iterations);
  }

  // Default to Argon2id (frontend-created shares)
  return deriveKeyArgon2(passcode, salt, {
    memory: params.memory as number,
    iterations: params.iterations as number,
    parallelism: params.parallelism as number,
    hashLength: params.hashLength as number,
  });
}

/**
 * Derive key from API key using PBKDF2
 * Uses fewer iterations since API keys are already high-entropy
 */
async function deriveKeyFromApiKey(
  apiKey: string,
  salt: Uint8Array,
): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encoder.encode(apiKey),
    "PBKDF2",
    false,
    ["deriveBits"],
  );

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      salt: salt as BufferSource,
      iterations: 10000, // Fewer iterations since API keys have high entropy
      hash: "SHA-256",
    },
    keyMaterial,
    256,
  );

  return new Uint8Array(derivedBits);
}

// KDF params for API key derivation (exported for consistency with server)
export const API_KEY_KDF_PARAMS = {
  type: "PBKDF2",
  iterations: 10000,
  hash: "SHA-256",
  hashLength: 32,
};

export interface EncryptedData {
  blob: string; // Base64 encrypted content (or plaintext for public)
  wrappedCek: string; // Base64 wrapped content encryption key (empty for public)
  nonce: string; // Base64 nonce/IV (empty for public)
  kdfSalt: string; // Base64 salt for key derivation (empty for public)
  kdfParams: string; // JSON stringified KDF parameters (empty for public)
  cipher: string; // Cipher identifier ('AES-256-GCM' or 'NONE')
  wrappedCekApiKey?: string; // Base64 CEK wrapped with API key-derived key (for owner)
  kdfSaltApiKey?: string; // Base64 salt for API key KDF
}

const CIPHER = "AES-256-GCM";
const CIPHER_NONE = "NONE"; // Public (unencrypted) shares

/**
 * Generate a random Content Encryption Key (CEK)
 */
function generateCek(): Uint8Array {
  const cek = new Uint8Array(32); // 256 bits
  crypto.getRandomValues(cek);
  return cek;
}

/**
 * Generate a random nonce/IV for AES-GCM
 */
function generateNonce(): Uint8Array {
  const nonce = new Uint8Array(12); // 96 bits recommended for AES-GCM
  crypto.getRandomValues(nonce);
  return nonce;
}

/**
 * Import raw key bytes as CryptoKey
 */
async function importKey(
  keyBytes: Uint8Array,
  usages: KeyUsage[],
): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    keyBytes as unknown as BufferSource,
    { name: "AES-GCM" },
    false,
    usages,
  );
}

/**
 * Import key for AES-KW (key wrapping)
 */
async function importWrapKey(keyBytes: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    keyBytes as unknown as BufferSource,
    { name: "AES-KW" },
    false,
    ["wrapKey", "unwrapKey"],
  );
}

/**
 * Wrap the CEK with the passcode-derived key
 */
async function wrapCek(
  cek: Uint8Array,
  wrapKeyBytes: Uint8Array,
): Promise<Uint8Array> {
  const wrapKey = await importWrapKey(wrapKeyBytes);
  const cekKey = await crypto.subtle.importKey(
    "raw",
    cek as unknown as BufferSource,
    { name: "AES-GCM" },
    true,
    ["encrypt", "decrypt"],
  );

  const wrappedBuffer = await crypto.subtle.wrapKey("raw", cekKey, wrapKey, {
    name: "AES-KW",
  });

  return new Uint8Array(wrappedBuffer);
}

/**
 * Unwrap the CEK with the passcode-derived key
 * @param usages Key usages for the unwrapped key (default: ['decrypt'])
 */
async function unwrapCek(
  wrappedCek: Uint8Array,
  unwrapKeyBytes: Uint8Array,
  usages: KeyUsage[] = ["decrypt"],
): Promise<CryptoKey> {
  const unwrapKey = await importWrapKey(unwrapKeyBytes);

  return crypto.subtle.unwrapKey(
    "raw",
    wrappedCek as unknown as BufferSource,
    unwrapKey,
    { name: "AES-KW" },
    { name: "AES-GCM" },
    false,
    usages,
  );
}

/**
 * Encrypt content with AES-256-GCM
 *
 * Flow:
 * 1. Generate random CEK
 * 2. Generate salt and derive passcode key with Argon2id
 * 3. Wrap CEK with passcode-derived key
 * 4. Optionally wrap CEK with API key-derived key (for owner access)
 * 5. Encrypt content with CEK
 *
 * @param content Content to encrypt
 * @param passcode User's passcode
 * @param apiKey Optional API key for dual-wrapping (allows owner to decrypt without passcode)
 */
export async function encryptContent(
  content: string,
  passcode: string,
  apiKey?: string,
): Promise<EncryptedData> {
  // 1. Generate random CEK
  const cek = generateCek();

  // 2. Generate salt and derive key from passcode (always use Argon2 for frontend)
  const salt = generateSalt();
  const passcodeKey = await deriveKeyArgon2(passcode, salt, DEFAULT_KDF_PARAMS);

  // 3. Wrap CEK with passcode-derived key
  const wrappedCek = await wrapCek(cek, passcodeKey);

  // 4. Optionally wrap CEK with API key-derived key
  let wrappedCekApiKey: string | undefined;
  let kdfSaltApiKey: string | undefined;

  if (apiKey) {
    const saltApiKey = generateSalt();
    const apiKeyDerivedKey = await deriveKeyFromApiKey(apiKey, saltApiKey);
    const wrappedCekApiKeyBytes = await wrapCek(cek, apiKeyDerivedKey);
    wrappedCekApiKey = arrayBufferToBase64(
      wrappedCekApiKeyBytes.buffer as ArrayBuffer,
    );
    kdfSaltApiKey = arrayBufferToBase64(saltApiKey.buffer as ArrayBuffer);
  }

  // 5. Encrypt content with CEK
  const nonce = generateNonce();
  const contentKey = await importKey(cek, ["encrypt"]);
  const encoder = new TextEncoder();
  const contentBytes = encoder.encode(content);

  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce as unknown as BufferSource },
    contentKey,
    contentBytes,
  );

  return {
    blob: arrayBufferToBase64(encryptedBuffer),
    wrappedCek: arrayBufferToBase64(wrappedCek.buffer as ArrayBuffer),
    nonce: arrayBufferToBase64(nonce.buffer as ArrayBuffer),
    kdfSalt: arrayBufferToBase64(salt.buffer as ArrayBuffer),
    kdfParams: serializeKdfParams(DEFAULT_KDF_PARAMS),
    cipher: CIPHER,
    wrappedCekApiKey,
    kdfSaltApiKey,
  };
}

/**
 * Decrypt content with AES-256-GCM
 *
 * Flow:
 * 1. Derive passcode key with Argon2id
 * 2. Unwrap CEK with passcode-derived key
 * 3. Decrypt content with CEK
 */
export async function decryptContent(
  encryptedBlob: string,
  wrappedCek: string,
  nonce: string,
  kdfSalt: string,
  kdfParams: string,
  passcode: string,
): Promise<string> {
  // 1. Derive passcode key
  const salt = new Uint8Array(base64ToArrayBuffer(kdfSalt));
  const params = JSON.parse(kdfParams);
  const passcodeKey = await deriveKey(passcode, salt, params);

  // 2. Unwrap CEK
  const wrappedCekBytes = new Uint8Array(base64ToArrayBuffer(wrappedCek));
  const cek = await unwrapCek(wrappedCekBytes, passcodeKey);

  // 3. Decrypt content
  const nonceBytes = new Uint8Array(base64ToArrayBuffer(nonce));
  const encryptedBytes = base64ToArrayBuffer(encryptedBlob);

  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: nonceBytes as unknown as BufferSource },
    cek,
    encryptedBytes,
  );

  const decoder = new TextDecoder();
  return decoder.decode(decryptedBuffer);
}

/**
 * Decrypt content using API key instead of passcode
 * Used by share owners to view their own shares without entering passcode
 *
 * @param encryptedBlob Base64 encrypted content
 * @param wrappedCekApiKey Base64 CEK wrapped with API key-derived key
 * @param nonce Base64 nonce/IV
 * @param kdfSaltApiKey Base64 salt for API key KDF
 * @param apiKey The API key
 */
export async function decryptWithApiKey(
  encryptedBlob: string,
  wrappedCekApiKey: string,
  nonce: string,
  kdfSaltApiKey: string,
  apiKey: string,
): Promise<string> {
  // 1. Derive key from API key
  const salt = new Uint8Array(base64ToArrayBuffer(kdfSaltApiKey));
  const apiKeyDerivedKey = await deriveKeyFromApiKey(apiKey, salt);

  // 2. Unwrap CEK
  const wrappedCekBytes = new Uint8Array(base64ToArrayBuffer(wrappedCekApiKey));
  const cek = await unwrapCek(wrappedCekBytes, apiKeyDerivedKey);

  // 3. Decrypt content
  const nonceBytes = new Uint8Array(base64ToArrayBuffer(nonce));
  const encryptedBytes = base64ToArrayBuffer(encryptedBlob);

  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: nonceBytes as unknown as BufferSource },
    cek,
    encryptedBytes,
  );

  const decoder = new TextDecoder();
  return decoder.decode(decryptedBuffer);
}

/**
 * Create plaintext content for public shares (no encryption)
 * Content is base64 encoded for consistent API handling
 */
export function createPlaintextContent(content: string): EncryptedData {
  const encoder = new TextEncoder();
  const contentBytes = encoder.encode(content);

  return {
    blob: arrayBufferToBase64(contentBytes.buffer as ArrayBuffer),
    wrappedCek: "", // Not used for public shares
    nonce: "", // Not used for public shares
    kdfSalt: "", // Not used for public shares
    kdfParams: "", // Not used for public shares
    cipher: CIPHER_NONE,
  };
}

/**
 * Decode plaintext content from public shares
 */
export function decodePlaintextContent(blob: string): string {
  const bytes = base64ToArrayBuffer(blob);
  const decoder = new TextDecoder();
  return decoder.decode(bytes);
}

/**
 * Re-encrypt content for modifiable shares
 * Uses existing crypto params (same CEK, salt) but generates new nonce
 *
 * @param content New content to encrypt
 * @param wrappedCek Base64 wrapped CEK from original share
 * @param kdfSalt Base64 KDF salt from original share
 * @param kdfParams JSON stringified KDF params from original share
 * @param passcode User's passcode
 * @returns New blob and nonce (wrappedCek stays the same)
 */
export async function reencryptContent(
  content: string,
  wrappedCekB64: string,
  kdfSalt: string,
  kdfParams: string,
  passcode: string,
): Promise<{ blob: string; nonce: string }> {
  // 1. Derive passcode key using same salt and params
  const salt = new Uint8Array(base64ToArrayBuffer(kdfSalt));
  const params = JSON.parse(kdfParams);
  const passcodeKey = await deriveKey(passcode, salt, params);

  // 2. Unwrap CEK to get the content encryption key (with encrypt permission for re-encryption)
  const wrappedCekBytes = new Uint8Array(base64ToArrayBuffer(wrappedCekB64));
  const cek = await unwrapCek(wrappedCekBytes, passcodeKey, ["encrypt"]);

  // 3. Encrypt content with CEK and a new nonce
  const nonce = generateNonce();
  const encoder = new TextEncoder();
  const contentBytes = encoder.encode(content);

  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce as unknown as BufferSource },
    cek,
    contentBytes,
  );

  return {
    blob: arrayBufferToBase64(encryptedBuffer),
    nonce: arrayBufferToBase64(nonce.buffer as ArrayBuffer),
  };
}

/**
 * Re-encrypt content using API key (for updating modifiable shares)
 * Uses the wrapped CEK from API key auth to re-encrypt with a new nonce
 */
export async function reencryptWithApiKey(
  content: string,
  wrappedCekApiKeyB64: string,
  kdfSaltApiKeyB64: string,
  apiKey: string,
): Promise<{ blob: string; nonce: string }> {
  // 1. Derive key from API key
  const salt = new Uint8Array(base64ToArrayBuffer(kdfSaltApiKeyB64));
  const apiKeyDerivedKey = await deriveKeyFromApiKey(apiKey, salt);

  // 2. Unwrap CEK to get the content encryption key (with encrypt permission for re-encryption)
  const wrappedCekBytes = new Uint8Array(
    base64ToArrayBuffer(wrappedCekApiKeyB64),
  );
  const cek = await unwrapCek(wrappedCekBytes, apiKeyDerivedKey, ["encrypt"]);

  // 3. Encrypt content with CEK and a new nonce
  const nonce = generateNonce();
  const encoder = new TextEncoder();
  const contentBytes = encoder.encode(content);

  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce as unknown as BufferSource },
    cek,
    contentBytes,
  );

  return {
    blob: arrayBufferToBase64(encryptedBuffer),
    nonce: arrayBufferToBase64(nonce.buffer as ArrayBuffer),
  };
}

/**
 * Encrypt the passcode with the API key
 * This allows the owner to retrieve the passcode later from the shares list
 * Format: base64(salt:nonce:ciphertext)
 */
export async function encryptPasscodeWithApiKey(
  passcode: string,
  apiKey: string,
): Promise<string> {
  // Generate salt and derive key from API key
  const salt = new Uint8Array(16);
  crypto.getRandomValues(salt);
  const derivedKey = await deriveKeyFromApiKey(apiKey, salt);

  // Import as AES-GCM key for encryption
  const encryptKey = await crypto.subtle.importKey(
    "raw",
    derivedKey as BufferSource,
    { name: "AES-GCM" },
    false,
    ["encrypt"],
  );

  // Generate nonce and encrypt
  const nonce = new Uint8Array(12);
  crypto.getRandomValues(nonce);

  const passcodeBytes = new TextEncoder().encode(passcode);
  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce as unknown as BufferSource },
    encryptKey,
    passcodeBytes,
  );

  // Combine salt:nonce:ciphertext and encode as base64
  const combined = new Uint8Array(
    salt.length + nonce.length + encryptedBuffer.byteLength,
  );
  combined.set(salt, 0);
  combined.set(nonce, salt.length);
  combined.set(new Uint8Array(encryptedBuffer), salt.length + nonce.length);

  return arrayBufferToBase64(combined.buffer as ArrayBuffer);
}

/**
 * Decrypt the wrapped passcode using the API key
 * The wrapped passcode format is: base64(salt:nonce:ciphertext)
 * where salt is 16 bytes, nonce is 12 bytes, and the rest is ciphertext
 */
export async function decryptPasscodeWithApiKey(
  wrappedPasscode: string,
  apiKey: string,
): Promise<string> {
  // Decode the combined data
  const combined = new Uint8Array(base64ToArrayBuffer(wrappedPasscode));

  // Extract salt (first 16 bytes), nonce (next 12 bytes), and ciphertext
  const salt = combined.slice(0, 16);
  const nonce = combined.slice(16, 28);
  const ciphertext = combined.slice(28);

  // Derive key from API key
  const derivedKey = await deriveKeyFromApiKey(apiKey, salt);

  // Import as AES-GCM key for decryption
  const decryptKey = await crypto.subtle.importKey(
    "raw",
    derivedKey as BufferSource,
    { name: "AES-GCM" },
    false,
    ["decrypt"],
  );

  // Decrypt
  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: nonce as unknown as BufferSource },
    decryptKey,
    ciphertext,
  );

  return new TextDecoder().decode(decryptedBuffer);
}
